import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { isEmpty, isEqual, omit } from 'lodash';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, pluck } from 'rxjs/operators';

import { CashDrawerSummaryItemModel } from '@ag-grid-reports/cash-drawer-summary-report/models/cashDrawerSummaryItem.model';
import {
    IDepositDetailRequest,
    IDepositDetailResponse,
    IDepositSummaryRequest,
    IDepositSummaryResponse,
} from '@ag-grid-reports/deposit-summary-report/models/deposit-summary.types';
import { EmployeeSalesItemModel } from '@ag-grid-reports/employee-sales-report/models/employeeSalesItem.model';
import {
    IGiftCardActivityRequest,
    IGiftCardActivityResponse,
} from '@ag-grid-reports/gift-card-activity-report/models/gift-card-activity.types';
import {
    INoSaleByEmployeeRequest,
    INoSaleByEmployeeResponse,
} from '@ag-grid-reports/no-sale-by-employee-report/models/no-sale-by-employee.types';
import {
    IDeletedItemModel,
    IDetailDataModel,
    IDiscountDataModel,
    IFundraiserDataModel,
    IPaymentDataModel,
    ISplitCombineData,
} from '@ag-grid-reports/order-explorer-detail/models/order-explorer-detail.model';
import {
    IPaidInOutDetailRequest,
    IPaidInOutDetailResponse,
} from '@ag-grid-reports/paid-in-out-detail-report/paid-in-out-detail.types';
import { PrimaryLaborByHourItemModel } from '@ag-grid-reports/primary-labor-by-hour-report/models/primaryLaborByHourItem.model';
import {
    DrawerSectionModel,
    GroupsSectionModel,
} from '@containers/reports/report/custom-reports/sales-detail-report/models/dataModels';
import { selectLoader } from '@rpt-ngrx/selectors/ui.selector';
import { ErrorHandlerService } from '@services/helper';
import { CustomHttpClient, HttpCancelService } from '@services/http-client';
import { DetailParam, ISortModelField } from '@shared/types';
import { IValuesOfFilters } from '@shared/types/filters';
import { IReport, SuiteOrderSummaryDetail, SuiteOrderSummaryDetailResp } from '@shared/types/report';
import { rowLimit } from '@shared/utils';

@Injectable()
export class CustomReportsService {
    private contentLoader$: Observable<boolean>;
    private isCanceled: boolean;

    private previousRequest = {
        url: '',
        data: {},
    };
    private companyId: string;

    constructor(
        public httpCancelService: HttpCancelService,
        private http: CustomHttpClient,
        private errorHandlerService: ErrorHandlerService,
        private router: Router,
        private store: Store,
        private translations: TranslateService,
    ) {
        this.contentLoader$ = this.store.select(selectLoader);
        this.contentLoader$.subscribe((res) => {
            if (this.isCanceled === false && !res) {
                // this.httpCancelService.cancelPendingRequests();
            }
            this.isCanceled = !res;
        });
    }

    public setCompanyId(id: string) {
        this.companyId = id;
    }

    public getCompanyId(): string {
        return this.companyId;
    }

    public formatStrforSqlILike(str: string | undefined): string {
        // reference https://docs.databricks.com/sql/language-manual/functions/ilike.html
        const specialSymbols = ['\\', '_', '%'];

        specialSymbols.forEach((symbol) => {
            str = (str || '').replaceAll(symbol, `\\${symbol}`);
        });

        return str;
    }

    // Speed of Service
    public fetchSpeedOfServiceReport(sites: string[] | string, dateFrom: string, dateTo: string) {
        const headers = new HttpHeaders({
            'x-company-id': this.companyId,
        });
        const body = {
            sites: sites,
            date_from: dateFrom,
            date_to: dateTo,
        };
        return this.http
            .post('/custom-reports/speed-of-service/data', body, { headers })
            .pipe(catchError((error: any) => this.errorHandlerService.getErrorMessage(error)));
    }

    // Sales and Labor report
    public fetchSalesAndLaborHours(sites, dateFrom, dateTo) {
        const headers = new HttpHeaders({
            'x-company-id': this.companyId,
        });
        const body = {
            sites: sites,
            date_from: dateFrom,
            date_to: dateTo,
        };
        return this.http
            .post('/custom-reports/sales-and-labor/by-hours', body, { headers })
            .pipe(catchError((error: any) => this.errorHandlerService.getErrorMessage(error)));
    }

    public fetchSalesAndLaborHeader(sites, dateFrom, dateTo) {
        const headers = new HttpHeaders({
            'x-company-id': this.companyId,
        });
        const body = {
            sites: sites,
            date_from: dateFrom,
            date_to: dateTo,
        };
        return this.http
            .post('/custom-reports/sales-and-labor/header', body, { headers })
            .pipe(catchError((error: any) => this.errorHandlerService.getErrorMessage(error)));
    }

    public fetchSalesAndLabor(sites, dateFrom, dateTo) {
        return forkJoin([
            this.fetchSalesAndLaborHours(sites, dateFrom, dateTo),
            this.fetchSalesAndLaborHeader(sites, dateFrom, dateTo),
        ]).pipe(catchError((error: any) => this.errorHandlerService.getErrorMessage(error)));
    }

    // Sales Analysis report
    public fetchSalesAnalysis(sites, date, reportGroupingMethod) {
        const headers = new HttpHeaders({
            'x-company-id': this.companyId,
        });
        const body = { sites, business_date: date, reportGroupingMethod };
        return this.http
            .post('/custom-reports/sales-analysis/data', body, { headers })
            .pipe(catchError((error: any) => this.errorHandlerService.getErrorMessage(error)));
    }

    // Enterprise Sales Summary report
    public fetchEnterpriseSalesSummary(request) {
        const { company, sites, date_from, date_to, events = 'None' } = request;
        const headers = new HttpHeaders({
            'x-company-id': company,
            'x-event-ids': events,
        });
        const body = {
            date_from,
            date_to,
            sites,
            row_limit: rowLimit,
        };

        return this.http
            .post('/custom-reports/enterprise-sales/data', body, { headers })
            .pipe(catchError((error: any) => this.errorHandlerService.getErrorMessage(error)));
    }

    public fetchEmployeeSales(request) {
        const { company, sites, date_from, date_to, events = 'None' } = request;
        const headers = new HttpHeaders({
            'x-company-id': company,
            'x-event-ids': events,
        });
        const body = {
            date_from,
            date_to,
            sites,
            row_limit: rowLimit,
        };

        return this.http
            .post<{
                data: Array<EmployeeSalesItemModel>;
                build: string;
                rows_amount: number;
                total_amount: number;
                payTypes: Array<string>;
            }>('/custom-reports/employee-sales', body, { headers })
            .pipe(
                catchError((err: { statusText: string }) => {
                    return of({
                        data: [],
                        payTypes: [],
                        build: '',
                        rows_amount: 0,
                        total_amount: 0,
                        error: this.errorHandlerService.getErrorMessage(err),
                    });
                }),
            );
    }

    public fetchPrimaryLaborByHour(request) {
        const { company, sites, date, display_option, eod, reportGroupingMethod, hide_future } = request;
        const headers = new HttpHeaders({
            'x-company-id': company,
        });
        const body = {
            date,
            sites,
            display_option,
            eod,
            row_limit: rowLimit,
            reportGroupingMethod,
            hide_future,
        };

        return this.http
            .post<{
                data: Array<PrimaryLaborByHourItemModel>;
                descriptorsMap: Array<string>;
                build: string;
            }>('/custom-reports/primary-labor-by-hour', body, { headers })
            .pipe(
                catchError((err: { statusText: string }) => {
                    return of({
                        data: [],
                        descriptorsMap: [],
                        build: '',
                        error: this.errorHandlerService.getErrorMessage(err),
                    });
                }),
            );
    }

    public fetchSalesDetailReport(request): Observable<any> {
        const {
            company,
            sites,
            events = 'None',
            dateFrom,
            dateTo,
            drawerGrouping,
            destination_id,
            day_part_id,
        } = request;
        const headers = new HttpHeaders({
            'x-company-id': company,
            'x-event-ids': events,
        });

        const body = {
            date_from: dateFrom,
            date_to: dateTo,
            drawerGrouping,
            sites,
            destination_id,
            day_part_id,
        };

        return this.http
            .post<
                Array<DrawerSectionModel> | Array<GroupsSectionModel>
            >('/custom-reports/sales-detail', body, { headers })
            .pipe(catchError((error: any) => this.errorHandlerService.getErrorMessage(error)));
    }

    // Order Explorer report
    public fetchOrderExplorer(request) {
        const {
            company,
            sites,
            dateFrom,
            dateTo,
            orderNumber,
            cardNumber,
            tableNumber,
            employee,
            terminal_id,
            time,
            reportGroupingMethod,
        } = request;
        const headers = new HttpHeaders({
            'x-company-id': company,
        });

        const body = {
            date_from: dateFrom,
            date_to: dateTo,
            employee,
            terminal_id,
            time,
            row_limit: rowLimit,
            sites,
            reportGroupingMethod,
        };

        if (orderNumber) {
            body['order_number'] = orderNumber;
        }

        if (cardNumber) {
            body['card_number'] = cardNumber;
        }

        if (tableNumber) {
            body['table_number'] = tableNumber;
        }

        return this.http
            .post('/custom-reports/order-explorer/data', body, { headers: headers })
            .pipe(catchError((error: any) => this.errorHandlerService.getErrorMessage(error)));
    }

    public fetchOrderExplorerGeneralDetail(
        company,
        sites,
        orderId,
        businessDate,
        combined_from,
        split_from,
    ): Observable<{ data: Array<IDetailDataModel>; splitCombinedOrders: Array<ISplitCombineData> }> {
        const headers = new HttpHeaders({
            'x-company-id': company,
        });
        const body = {
            business_date: businessDate,
            combined_from,
            split_from,
            sites,
        };
        return this.http
            .post<{
                data: Array<IDetailDataModel>;
                splitCombinedOrders: Array<ISplitCombineData>;
            }>(`/custom-reports/order-explorer/detail/${orderId}`, body, { headers })
            .pipe(
                catchError(() =>
                    of({} as { data: Array<IDetailDataModel>; splitCombinedOrders: Array<ISplitCombineData> }),
                ),
            );
    }

    public fetchOrderExplorerPaymentsDetails(
        company,
        sites,
        orderId,
        businessDate,
    ): Observable<Array<IPaymentDataModel>> {
        const headers = new HttpHeaders({
            'x-company-id': company,
        });
        const body = {
            business_date: businessDate,
            sites,
        };
        return this.http
            .post<{
                data: Array<IPaymentDataModel>;
            }>(`/custom-reports/order-explorer/payments/${orderId}`, body, { headers })
            .pipe(
                pluck('data'),
                catchError(() => of([] as Array<IPaymentDataModel>)),
            );
    }

    public fetchOrderExplorerFundraisingDetails(
        company,
        sites,
        orderId,
        businessDate,
    ): Observable<Array<IFundraiserDataModel>> {
        const headers = new HttpHeaders({
            'x-company-id': company,
        });
        const body = {
            business_date: businessDate,
            sites,
        };
        return this.http
            .post<{
                data: Array<IFundraiserDataModel>;
            }>(`/custom-reports/order-explorer/fundraising/${orderId}`, body, { headers })
            .pipe(
                pluck('data'),
                catchError(() => of([] as Array<IFundraiserDataModel>)),
            );
    }

    public fetchOrderExplorerDiscountsDetails(
        company,
        sites,
        orderId,
        businessDate,
    ): Observable<Array<IDiscountDataModel>> {
        const headers = new HttpHeaders({
            'x-company-id': company,
        });
        const body = {
            business_date: businessDate,
            sites,
        };
        return this.http
            .post<{
                data: Array<IDiscountDataModel>;
            }>(`/custom-reports/order-explorer/discount/${orderId}`, body, { headers })
            .pipe(
                pluck('data'),
                catchError(() => of([] as Array<IDiscountDataModel>)),
            );
    }

    public fetchOrderExplorerDeletedItemsDetails(
        company,
        sites,
        orderId,
        businessDate,
    ): Observable<Array<IDeletedItemModel>> {
        const headers = new HttpHeaders({
            'x-company-id': company,
        });
        const body = {
            business_date: businessDate,
            sites,
        };
        return this.http
            .post<{ data: Array<any> }>(`/custom-reports/order-explorer/deleted/${orderId}`, body, { headers })
            .pipe(
                pluck('data'),
                catchError(() => of([] as Array<any>)),
            );
    }

    public fetchOrderExplorerDetail(
        company,
        sites,
        orderId,
        businessDate,
        combined_from?,
        split_from?,
    ): Observable<{
        detailsData: { data: Array<IDetailDataModel>; splitCombinedOrders: Array<ISplitCombineData> };
        paymentsDetails: Array<IPaymentDataModel>;
        discountDetails: Array<IDiscountDataModel>;
        deletedItemsDetails: Array<IDeletedItemModel>;
        fundraiserDetails: Array<IFundraiserDataModel>;
    }> {
        return forkJoin([
            this.fetchOrderExplorerGeneralDetail(company, sites, orderId, businessDate, combined_from, split_from),
            this.fetchOrderExplorerPaymentsDetails(company, sites, orderId, businessDate),
            this.fetchOrderExplorerDiscountsDetails(company, sites, orderId, businessDate),
            this.fetchOrderExplorerDeletedItemsDetails(company, sites, orderId, businessDate),
            this.fetchOrderExplorerFundraisingDetails(company, sites, orderId, businessDate),
        ]).pipe(
            map(([detailsData, paymentsDetails, discountDetails, deletedItemsDetails, fundraiserDetails]) => {
                return { detailsData, paymentsDetails, discountDetails, deletedItemsDetails, fundraiserDetails };
            }),
            catchError(() =>
                of(
                    {} as {
                        detailsData: { data: Array<IDetailDataModel>; splitCombinedOrders: Array<ISplitCombineData> };
                        paymentsDetails: Array<IPaymentDataModel>;
                        discountDetails: Array<IDiscountDataModel>;
                        deletedItemsDetails: Array<IDeletedItemModel>;
                        fundraiserDetails: Array<IFundraiserDataModel>;
                    },
                ),
            ),
        );
    }

    // Employee Discount report
    public fetchEmployeeDiscount(request) {
        const { company, sites, dateFrom, dateTo } = request;
        const headers = new HttpHeaders({
            'x-company-id': company,
        });
        const body = {
            date_from: dateFrom,
            date_to: dateTo,
            row_limit: rowLimit,
            sites,
        };

        return this.http
            .post('/custom-reports/employee-discount/data', body, { headers })
            .pipe(catchError((error: any) => this.errorHandlerService.getErrorMessage(error)));
    }

    public fetchEmployeeScorecard(request) {
        const { company, sites, date_from, date_to, reportId, destinations } = request;
        const headers = new HttpHeaders({
            'x-company-id': company,
        });
        const body = {
            reportId: reportId,
            date_from,
            date_to,
            sites,
            destinations,
            row_limit: rowLimit,
        };

        return this.http
            .post('/custom-reports/employee-scorecard', body, { headers })
            .pipe(catchError((error: any) => this.errorHandlerService.getErrorMessage(error)));
    }

    public fetchPaidOutDetailData(request): Observable<any> {
        const { company, sites, date_from, date_to } = request;
        const headers = new HttpHeaders({
            'x-company-id': company,
        });
        const body = {
            date_from: date_from,
            date_to: date_to,
            sites,
            row_limit: rowLimit,
        };

        return this.http
            .post('/custom-reports/paid-out-tips-detail/data', body, { headers })
            .pipe(catchError((error: any) => this.errorHandlerService.getErrorMessage(error)));
    }

    // Order Payments report
    public fetchOrderPayments(request) {
        const {
            company,
            sites,
            date_from,
            date_to,
            trans_type,
            payment_status,
            events = 'None',
            payment_type,
        } = request;
        const headers = new HttpHeaders({
            'x-company-id': company,
            'x-event-ids': events,
        });
        const body = {
            date_from,
            date_to,
            payment_status,
            trans_type,
            row_limit: rowLimit,
            sites,
            payment_type,
        };

        return this.http
            .post('/custom-reports/order-payments/data', body, { headers })
            .pipe(catchError((error: any) => this.errorHandlerService.getErrorMessage(error)));
    }

    public fetchOrderSummaryData(request) {
        const { company, sites, dateTo, dateFrom, orderNumber } = request;
        const headers = new HttpHeaders({
            'x-company-id': company,
        });
        const body = {
            date_from: dateFrom,
            date_to: dateTo,
            order_number: orderNumber,
            row_limit: rowLimit,
            sites,
        };

        return this.http
            .post('/custom-reports/order-summary/data', body, { headers })
            .pipe(catchError((error: any) => this.errorHandlerService.getErrorMessage(error)));
    }

    public fetchOrderSummaryCount(request) {
        const { company, sites, dateTo, dateFrom, orderNumber } = request;
        const headers = new HttpHeaders({
            'x-company-id': company,
        });
        const body = {
            date_from: dateFrom,
            date_to: dateTo,
            order_number: orderNumber,
            sites,
        };

        return this.http
            .post('/custom-reports/order-summary/count', body, { headers })
            .pipe(catchError((error: any) => this.errorHandlerService.getErrorMessage(error)));
    }

    public fetchOrderSummaryDetail(request) {
        const { company_id, order_number } = request;
        const headers = new HttpHeaders({
            'x-company-id': company_id,
        });

        return this.http
            .post(`/custom-reports/order-summary/detail/${order_number}`, {}, { headers })
            .pipe(catchError((error: any) => this.errorHandlerService.getErrorMessage(error)));
    }

    public fetchInventoryItemCount(request) {
        const { company, sites, options, isCompanyView } = request;
        const headers = new HttpHeaders({
            'x-company-id': company,
        });
        const body = {
            item_name: this.formatStrforSqlILike(options.Item_Name || 'All'),
            item_number: this.formatStrforSqlILike(options.Item_Number || 'All'),
        };

        if (!isCompanyView) body['sites'] = sites;

        return this.http
            .post(`/custom-reports/inventory-item-listing/${isCompanyView ? 'company-view-' : ''}count`, body, {
                headers,
            })
            .pipe(catchError((error: any) => this.errorHandlerService.getErrorMessage(error)));
    }

    public fetchInventoryItemListingData(request) {
        const { company, sites, options, isCompanyView } = request;
        const headers = new HttpHeaders({
            'x-company-id': company,
        });
        const body = {
            item_name: this.formatStrforSqlILike(options.Item_Name || 'All'),
            item_number: this.formatStrforSqlILike(options.Item_Number || 'All'),
            row_limit: rowLimit,
        };
        if (!isCompanyView) body['sites'] = sites;

        return this.http
            .post(`/custom-reports/inventory-item-listing/${isCompanyView ? 'company-view-' : ''}data`, body, {
                headers,
            })
            .pipe(catchError((error: any) => this.errorHandlerService.getErrorMessage(error)));
    }

    public fetchProductListingData(request) {
        const { company, sites, options, costMethod, isCompanyView } = request;
        const headers = new HttpHeaders({
            'x-company-id': company,
        });
        const body = {
            productId: this.formatStrforSqlILike(options.Product_ID),
            row_limit: rowLimit,
            costMethod,
        };
        if (!isCompanyView) body['sites'] = sites;

        return this.http
            .post(`/custom-reports/product-listing/${isCompanyView ? 'company-view-' : ''}data`, body, { headers })
            .pipe(catchError((error: any) => this.errorHandlerService.getErrorMessage(error)));
    }

    public fetchProductListingCount(request) {
        const { company, sites, options } = request;
        const headers = new HttpHeaders({
            'x-company-id': company,
        });
        const body = {
            productId: this.formatStrforSqlILike(options.Product_ID),
            sites,
        };

        return this.http
            .post('/custom-reports/product-listing/count', body, { headers })
            .pipe(catchError((error: any) => this.errorHandlerService.getErrorMessage(error)));
    }

    public fetchProductIngredientsList(request) {
        const { company, recipeId, costMethod } = request;
        const headers = new HttpHeaders({
            'x-company-id': company,
        });

        return this.http
            .get(`/custom-reports/product-listing/${recipeId}/ingredients-list?costMethod=${costMethod}`, { headers })
            .pipe(catchError((error: any) => this.errorHandlerService.getErrorMessage(error)));
    }

    public fetchInventoryCountData(request) {
        const { company, sites, dateFrom, dateTo, unit_option, options, inventory_count_group_id } = request;
        const headers = new HttpHeaders({
            'x-company-id': company,
        });
        const body = {
            date_from: dateFrom,
            date_to: dateTo,
            item_id: this.formatStrforSqlILike(options.Item_Number),
            item_name: this.formatStrforSqlILike(options.Item_Name),
            row_limit: rowLimit,
            unit_option,
            inventory_count_group_id,
            sites,
        };
        return this.http
            .post('/custom-reports/inventory-count/data', body, { headers })
            .pipe(catchError((error: any) => this.errorHandlerService.getErrorMessage(error)));
    }

    public fetchInventoryUniqueCount(request) {
        const { company, sites, dateFrom, dateTo, options } = request;
        const headers = new HttpHeaders({
            'x-company-id': company,
        });
        const body = {
            date_from: dateFrom,
            date_to: dateTo,
            item_id: this.formatStrforSqlILike(options.Item_Number),
            item_name: this.formatStrforSqlILike(options.Item_Name),
            sites,
        };
        return this.http
            .post('/custom-reports/inventory-count/count', body, { headers })
            .pipe(catchError((error: any) => this.errorHandlerService.getErrorMessage(error)));
    }

    public isNewRequestData(newData: IValuesOfFilters | object): boolean {
        const newRequest = {
            data: newData,
            url: this.router.url,
        };
        if (isEqual(newRequest, this.previousRequest)) {
            return false;
        }

        this.previousRequest = { ...newRequest };
        return true;
    }

    public publishReport(
        company: string,
        gridOptions: { columns: any[]; sortModel: ISortModelField[] },
        parentReport: IReport,
        permissions,
        detailPageOptions?: { columns: any[] },
    ): Observable<any> {
        const headers = new HttpHeaders({
            'x-company-id': company,
        });
        const body = {
            gridOptions,
            parentReport,
            permissions,
            detailPageOptions,
        };
        if (!detailPageOptions) {
            delete body.detailPageOptions;
        }
        return this.http
            .post('/custom-reports/publish', body, { headers })
            .pipe(catchError((error: any) => this.errorHandlerService.getErrorMessage(error)));
    }

    public makeDefaultSchema(
        company: string,
        userId: string,
        gridOptions: { columns: any[]; sortModel: ISortModelField[] },
        report: IReport,
    ): Observable<any> {
        const { id, _id, entity_id, report_options_category_entity_id, name, reportID } = report;
        const headers = new HttpHeaders({
            'x-company-id': company,
        });
        const body = {
            userId,
            gridOptions,
            reportOptions: {
                id,
                _id,
                entity_id,
                report_options_category_entity_id,
                name,
                reportID,
            },
        };

        return this.http
            .post('/custom-reports/setReportDefault', body, { headers })
            .pipe(catchError((error: any) => this.errorHandlerService.getErrorMessage(error)));
    }

    public resetDefaultSchema(company: string, userId: string, report: IReport): Observable<any> {
        const { id, _id, entity_id, report_options_category_entity_id, name, reportID } = report;
        const headers = new HttpHeaders({
            'x-company-id': company,
        });
        const body = {
            userId,
            reportOptions: {
                id,
                _id,
                entity_id,
                report_options_category_entity_id,
                name,
                reportID,
                report_config: {},
            },
        };

        return this.http
            .post('/custom-reports/setReportDefault/reset', body, { headers })
            .pipe(catchError((error: any) => this.errorHandlerService.getErrorMessage(error)));
    }

    private replaceGuestLabel(data: any[], fields: string[]): any[] {
        if (!Array.isArray(data)) {
            return data;
        }
        const guestLabel = this.translations.instant('GUEST');
        const reg = /.+\s\(Guest\)$/;
        const replaceReg = /\(Guest\)$/;

        return data.map((row) => {
            fields.forEach((field) => {
                if (typeof row[field] === 'string' && reg.test(row[field])) {
                    row[field] = row[field].replace(replaceReg, () => guestLabel);
                }
            });
            return row;
        });
    }

    public fetchSuiteCateringProductionCount(request) {
        const { company } = request;
        const headers = new HttpHeaders({
            'x-company-id': company,
        });
        const params = {
            ...omit(request, ['company']),
        };

        return this.http.get('/custom-reports/suite-catering/production/list', { headers, params }).pipe(
            map((res: any) => ({ ...res, data: this.replaceGuestLabel(res.data, ['account_name']) })),
            catchError((error: any) => this.errorHandlerService.getErrorMessage(error)),
        );
    }

    public fetchSuiteCateringProductionDetails(request) {
        const { company } = request;
        const headers = new HttpHeaders({
            'x-company-id': company,
        });
        const params = {
            ...omit(request, ['company']),
        };

        return this.http
            .post('/custom-reports/suite-catering/production/detail', params, { headers })
            .pipe(catchError((error: any) => this.errorHandlerService.getErrorMessage(error)));
    }

    public fetchSuiteCateringProductionSummaryDetails(request) {
        const { company } = request;
        const headers = new HttpHeaders({
            'x-company-id': company,
        });
        const params = {
            ...omit(request, ['company']),
        };

        return this.http
            .post('/custom-reports/suite-catering/production/summary-detail', params, { headers })
            .pipe(catchError((error: any) => this.errorHandlerService.getErrorMessage(error)));
    }

    public fetchNotesAndQuestionsCount(request) {
        const { company } = request;
        const headers = new HttpHeaders({
            'x-company-id': company,
        });
        const params = {
            ...omit(request, ['company']),
        };

        return this.http.get('/custom-reports/suite-catering/notes-questions/list', { headers, params }).pipe(
            map((res: any) => ({ ...res, data: this.replaceGuestLabel(res.data, ['account_name']) })),
            catchError((error: any) => this.errorHandlerService.getErrorMessage(error)),
        );
    }

    public fetchNotesAndQuestionsDetails(request) {
        const { company, orderIds, eventId } = request;
        const headers = new HttpHeaders({
            'x-company-id': company,
        });
        const params = {
            ids: orderIds,
            event_id: eventId,
        };
        return this.http.post('/custom-reports/suite-catering/notes-questions/detail', params, { headers }).pipe(
            map((res: any) => ({ ...res, data: this.replaceGuestLabel(res.data, ['account_name']) })),
            catchError((error: any) => this.errorHandlerService.getErrorMessage(error)),
        );
    }

    public fetchTransferSummaryData(request) {
        const { company, dateFrom, dateTo, transfer_number, isSummaryView } = request;
        const headers = new HttpHeaders({
            'x-company-id': company,
        });
        const body = {
            date_from: dateFrom,
            date_to: dateTo,
            transfer_number: transfer_number,
            row_limit: rowLimit,
        };

        return this.http
            .post(`/custom-reports/transfer-summary/${isSummaryView ? '' : 'site-type-'}data`, body, { headers })
            .pipe(catchError((error: any) => this.errorHandlerService.getErrorMessage(error)));
    }

    public fetchTransferSummaryCount(request) {
        const { company, dateFrom, dateTo, transfer_number, isSummaryView } = request;
        const headers = new HttpHeaders({
            'x-company-id': company,
        });

        const body = {
            date_from: dateFrom,
            date_to: dateTo,
            transfer_number: transfer_number,
        };

        return this.http
            .post(`/custom-reports/transfer-summary/${isSummaryView ? '' : 'site-type-'}count`, body, { headers })
            .pipe(catchError((error: any) => this.errorHandlerService.getErrorMessage(error)));
    }

    public fetchTransferSummaryDetail(request) {
        const { company, transfer_number } = request;
        const headers = new HttpHeaders({
            'x-company-id': company,
        });

        return this.http
            .post(`/custom-reports/transfer-summary/detail/${transfer_number}`, {}, { headers })
            .pipe(catchError((error: any) => this.errorHandlerService.getErrorMessage(error)));
    }

    public fetchSuitePayment(request) {
        const { company } = request;
        const headers = new HttpHeaders({
            'x-company-id': company,
        });
        const params = {
            ...omit(request, ['company']),
        };

        return this.http.get('/custom-reports/suite-catering/suite-payments', { headers, params }).pipe(
            map((res: any) => ({ ...res, data: this.replaceGuestLabel(res.data, ['account_name']) })),
            catchError((error: any) => this.errorHandlerService.getErrorMessage(error)),
        );
    }

    public fetchSuitePaymentOrderCount(request) {
        const { company } = request;
        const headers = new HttpHeaders({
            'x-company-id': company,
        });
        const params = {
            ...omit(request, ['company']),
        };
        return this.http
            .get('/custom-reports/suite-catering/suite-payments-count', { headers, params })
            .pipe(catchError((error: any) => this.errorHandlerService.getErrorMessage(error)));
    }

    public fetchInventoryOnHandData(request) {
        const { company, sites, item_number, item_name, qoh_date, unit_option } = request;
        const headers = new HttpHeaders({
            'x-company-id': company,
        });
        const body = {
            item_number,
            item_name,
            row_limit: rowLimit,
            sites,
            qoh_date,
            unit_option,
        };

        return this.http
            .post('/custom-reports/inventory-on-hand/data', body, { headers })
            .pipe(catchError((error: any) => this.errorHandlerService.getErrorMessage(error)));
    }

    public fetchInventoryOnHandCount(request) {
        const { company, sites, item_number, item_name } = request;
        const headers = new HttpHeaders({
            'x-company-id': company,
        });
        const body = {
            item_number,
            item_name,
            sites,
        };

        return this.http
            .post('/custom-reports/inventory-on-hand/count', body, { headers })
            .pipe(catchError((error: any) => this.errorHandlerService.getErrorMessage(error)));
    }

    public fetchSuiteOrderSummaryCount(request) {
        const { company } = request;
        const headers = new HttpHeaders({
            'x-company-id': company,
        });
        const params = {
            ...omit(request, ['company']),
        };
        return this.http
            .post('/custom-reports/suite-catering/order-summary-count', params, { headers })
            .pipe(catchError((error: any) => this.errorHandlerService.getErrorMessage(error)));
    }

    public fetchSuiteOrderSummaryDetail(request: DetailParam) {
        const { company } = request;
        const headers = new HttpHeaders({
            'x-company-id': company,
        });
        const params = {
            ...omit(request, ['company']),
        };
        return this.http.post('/custom-reports/suite-catering/order-summary-detail', params, { headers }).pipe(
            pluck<SuiteOrderSummaryDetailResp, Array<SuiteOrderSummaryDetail>>('data'),
            map((data: any[]) => this.replaceGuestLabel(data, ['account_name'])),
            catchError((error: any) => this.errorHandlerService.getErrorMessage(error)),
        );
    }

    public fetchSuiteOrderSummary(request) {
        const { company } = request;

        const headers = new HttpHeaders({
            'x-company-id': company,
        });
        const params = {
            ...omit(request, ['company']),
        };

        return this.http.post('/custom-reports/suite-catering/order-summary', params, { headers }).pipe(
            map((res: any) => ({ ...res, data: this.replaceGuestLabel(res.data, ['account_name']) })),
            catchError((error: any) => this.errorHandlerService.getErrorMessage(error)),
        );
    }

    public fetchStandWorksheetClosingData(request) {
        const { company, event_id, event_status, sites, stand_worksheet_post_closure_setting } = request;
        const headers = new HttpHeaders({
            'x-company-id': company,
        });
        const body = {
            event_id,
            event_status,
            sites,
            stand_worksheet_post_closure_setting,
            row_limit: rowLimit,
        };

        return this.http
            .post('/custom-reports/stand-worksheet-closing/data', body, { headers })
            .pipe(catchError((error: any) => this.errorHandlerService.getErrorMessage(error)));
    }

    public fetchStandWorksheetClosingCount(request) {
        const { company, event_id, event_status, sites } = request;
        const headers = new HttpHeaders({
            'x-company-id': company,
        });
        const body = {
            event_id,
            event_status,
            sites,
        };

        return this.http
            .post('/custom-reports/stand-worksheet-closing/count', body, { headers })
            .pipe(catchError((error: any) => this.errorHandlerService.getErrorMessage(error)));
    }

    public fetchStandWorksheetClosingDetail(request) {
        const { company, eventId, siteId, stand_worksheet_post_closure_setting } = request;
        const headers = new HttpHeaders({
            'x-company-id': company,
        });
        const body = {
            event_id: eventId,
            site_id: siteId,
            stand_worksheet_post_closure_setting,
        };
        return this.http
            .post(`/custom-reports/stand-worksheet-closing/detail`, body, { headers })
            .pipe(catchError((error: any) => this.errorHandlerService.getErrorMessage(error)));
    }

    public fetchRecipeDepletionsData(request) {
        const { company, event_id, sites, date_from, date_to } = request;
        const headers = new HttpHeaders({
            'x-company-id': company,
        });
        const body = {
            event_id,
            sites,
            row_limit: rowLimit,
            date_from,
            date_to,
        };

        return this.http
            .post('/custom-reports/recipe-depletions/data', body, { headers })
            .pipe(catchError((error: any) => this.errorHandlerService.getErrorMessage(error)));
    }

    public fetchPhysicalTerminalSalesData(request) {
        const { company, isCompanyView } = request;
        const headers = new HttpHeaders({
            'x-company-id': company,
        });
        const body = {
            ...omit(request, ['company', 'isCompanyView']),
            row_limit: rowLimit,
        };

        return this.http
            .post(`/custom-reports/physical-terminal-sales/${isCompanyView ? 'company-view-' : ''}data`, body, {
                headers,
            })
            .pipe(catchError((error: any) => this.errorHandlerService.getErrorMessage(error)));
    }

    public fetchPhysicalTerminalSalesCount(request) {
        const { company, isCompanyView } = request;
        const headers = new HttpHeaders({
            'x-company-id': company,
        });
        const body = omit(request, ['company', 'isCompanyView']);

        return this.http
            .post(`/custom-reports/physical-terminal-sales/${isCompanyView ? 'company-view-' : ''}count`, body, {
                headers,
            })
            .pipe(catchError((error: any) => this.errorHandlerService.getErrorMessage(error)));
    }

    public fetchSalesOverShortData(request) {
        const { company, event_ids, sites, stand_worksheet_post_closure_setting } = request;
        const headers = new HttpHeaders({
            'x-company-id': company,
        });
        const body = {
            stand_worksheet_post_closure_setting,
            event_ids,
            sites,
            row_limit: rowLimit,
        };

        return this.http
            .post('/custom-reports/sales-over-short/data', body, { headers })
            .pipe(catchError((error: any) => this.errorHandlerService.getErrorMessage(error)));
    }

    public fetchSalesOverShortCount(request) {
        const { company, event_ids, sites } = request;
        const headers = new HttpHeaders({
            'x-company-id': company,
        });
        const body = {
            event_ids,
            sites,
        };

        return this.http
            .post('/custom-reports/sales-over-short/count', body, { headers })
            .pipe(catchError((error: any) => this.errorHandlerService.getErrorMessage(error)));
    }

    public fetchAccountInvoiceCount(request) {
        const { company } = request;
        const headers = new HttpHeaders({
            'x-company-id': company,
        });
        const body = {
            ...omit(request, ['company']),
        };

        return this.http
            .post('/custom-reports/suite-catering/invoice/count', body, { headers })
            .pipe(catchError((error: any) => this.errorHandlerService.getErrorMessage(error)));
    }

    public fetchAccountInvoiceList(request) {
        const { company } = request;
        const headers = new HttpHeaders({
            'x-company-id': company,
        });
        const body = {
            ...omit(request, ['company']),
        };

        return this.http.post('/custom-reports/suite-catering/invoice/list', body, { headers }).pipe(
            map((res: any) => ({ ...res, data: this.replaceGuestLabel(res.data, ['account_name']) })),
            catchError((error: any) => this.errorHandlerService.getErrorMessage(error)),
        );
    }

    public fetchAccountInvoiceDetails(request) {
        const { company } = request;
        const headers = new HttpHeaders({
            'x-company-id': company,
        });
        const body = {
            ...omit(request, ['company']),
        };

        return this.http.post('/custom-reports/suite-catering/invoice/detail', body, { headers }).pipe(
            map((res: any) => ({ ...res, data: this.replaceGuestLabel(res.data, ['account_name']) })),
            catchError((error: any) => this.errorHandlerService.getErrorMessage(error)),
        );
    }

    public fetchSuiteItemSalesData(request) {
        const { company } = request;
        const headers = new HttpHeaders({
            'x-company-id': company,
        });
        const body = {
            ...omit(request, ['company']),
        };

        return this.http.post('/custom-reports/suite-catering/suite-item-sales/data', body, { headers }).pipe(
            map((res: any) => ({ ...res, data: this.replaceGuestLabel(res.data, ['account']) })),
            catchError((error: any) => this.errorHandlerService.getErrorMessage(error)),
        );
    }

    public fetchSuiteItemSalesCount(request) {
        const { company } = request;
        const headers = new HttpHeaders({
            'x-company-id': company,
        });
        const body = {
            ...omit(request, ['company', 'row_limit']),
        };

        return this.http
            .post('/custom-reports/suite-catering/suite-item-sales/count', body, { headers })
            .pipe(catchError((error: any) => this.errorHandlerService.getErrorMessage(error)));
    }

    public fetchCashDrawerSummaryData(request): Observable<
        | {
              data: Array<CashDrawerSummaryItemModel>;
              rows_amount: number;
              total_amount: number;
              build: string;
          }
        | any
    > {
        const { company, sites, date_from, date_to, none_placeholder } = request;
        const headers = new HttpHeaders({
            'x-company-id': company,
        });
        const body = {
            sites,
            date_from,
            date_to,
            row_limit: rowLimit,
            none_placeholder,
        };

        return this.http
            .post<{
                data: Array<CashDrawerSummaryItemModel>;
                build: string;
                rows_amount: number;
                total_amount: number;
            }>('/custom-reports/cash-drawer-summary', body, { headers })
            .pipe(
                catchError((err: { statusText: string }) => {
                    return of({
                        data: [],
                        build: '',
                        rows_amount: 0,
                        total_amount: 0,
                        error: this.errorHandlerService.getErrorMessage(err),
                    });
                }),
            );
    }

    public fetchCashDrawerTransactionHistory(request): Observable<
        | {
              data: Array<CashDrawerSummaryItemModel>;
              page_amount: number;
              build: string;
          }
        | any
    > {
        const { company, sites, date_from, date_to, drawer_session_id, none_placeholder } = request;
        const headers = new HttpHeaders({
            'x-company-id': company,
        });
        const body = {
            sites,
            drawer_session_id,
            date_from,
            date_to,
            none_placeholder,
        };

        return this.http
            .post<{
                data: Array<CashDrawerSummaryItemModel>;
                build: string;
                page_amount: number;
            }>('/custom-reports/cash-drawer-summary/transaction-history', body, { headers })
            .pipe(
                catchError((err: { statusText: string }) => {
                    return of({
                        data: [],
                        build: '',
                        page_amount: 0,
                        error: this.errorHandlerService.getErrorMessage(err),
                    });
                }),
            );
    }

    public fetchPaidInOutDetailData(request: IPaidInOutDetailRequest): Observable<IPaidInOutDetailResponse | any> {
        const { company, sites, date_from, date_to, none_placeholder } = request;
        const headers = new HttpHeaders({
            'x-company-id': company,
        });
        const body = {
            sites,
            date_from,
            date_to,
            row_limit: rowLimit,
            none_placeholder,
        };

        return this.http.post<IPaidInOutDetailResponse>('/custom-reports/paid-in-out-detail', body, { headers }).pipe(
            catchError((err: { statusText: string }) => {
                return of({
                    data: [],
                    build: '',
                    total_amount: 0,
                    rows_amount: 0,
                    error: this.errorHandlerService.getErrorMessage(err),
                });
            }),
        );
    }

    public fetchDepositSummaryData(request: IDepositSummaryRequest): Observable<IDepositSummaryResponse | any> {
        const { company, sites, date_from, date_to } = request;
        const headers = new HttpHeaders({
            'x-company-id': company,
        });
        const body = {
            sites,
            date_from,
            date_to,
            row_limit: rowLimit,
        };

        return this.http.post<IDepositSummaryResponse>('/custom-reports/deposit-summary', body, { headers }).pipe(
            catchError((err: { statusText: string }) => {
                return of({
                    data: [],
                    build: '',
                    rows_amount: 0,
                    total_amount: 0,
                    error: this.errorHandlerService.getErrorMessage(err),
                });
            }),
        );
    }

    public fetchDepositDetailData(request: IDepositDetailRequest): Observable<IDepositDetailResponse | any> {
        const { company, sites, date_from, date_to, business_date, deposit_id } = request;
        const headers = new HttpHeaders({
            'x-company-id': company,
        });
        const body = {
            sites,
            date_from,
            date_to,
            business_date,
            deposit_id,
        };

        return this.http
            .post<IDepositDetailResponse>('/custom-reports/deposit-summary/deposit-detail', body, { headers })
            .pipe(
                catchError((err: { statusText: string }) => {
                    return of({
                        data: [],
                        build: '',
                        page_amount: 0,
                        error: this.errorHandlerService.getErrorMessage(err),
                    });
                }),
            );
    }

    public fetchGiftCardActivityData(request: IGiftCardActivityRequest): Observable<IGiftCardActivityResponse | any> {
        const { company, sites, date_from, date_to } = request;
        const headers = new HttpHeaders({
            'x-company-id': company,
        });
        const body = {
            sites,
            date_from,
            date_to,
            row_limit: rowLimit,
        };

        return this.http.post<IGiftCardActivityResponse>('/custom-reports/gift-card-activity', body, { headers }).pipe(
            catchError((error: { statusText: string }) => {
                return of({
                    build: '',
                    data: [],
                    page_amount: 0,
                    total_amount: 0,
                    error: this.errorHandlerService.getErrorMessage(error),
                } as IGiftCardActivityResponse);
            }),
        );
    }

    public fetchSuiteTipData(request) {
        const { company } = request;
        const headers = new HttpHeaders({
            'x-company-id': company,
        });
        const body = {
            ...omit(request, ['company']),
        };

        return this.http.post('/custom-reports/suite-catering/suite-tip/list', body, { headers }).pipe(
            map((res: any) => ({ ...res, data: this.replaceGuestLabel(res.data, ['account']) })),
            catchError((error: any) => this.errorHandlerService.getErrorMessage(error)),
        );
    }

    public fetchSuiteOrderAssignmentsData(request) {
        const { company } = request;
        const headers = new HttpHeaders({
            'x-company-id': company,
        });
        const body = {
            ...omit(request, ['company']),
        };

        return this.http.post('/custom-reports/suite-catering/order-assignments/list', body, { headers }).pipe(
            map((res: any) => ({ ...res, data: this.replaceGuestLabel(res.data, ['account_name']) })),
            catchError((error: any) => this.errorHandlerService.getErrorMessage(error)),
        );
    }

    public fetchTransferByItemData(request) {
        const { company, sites, dateFrom, dateTo, unit_option } = request;
        const headers = new HttpHeaders({
            'x-company-id': company,
        });
        const body = {
            site_ids: sites,
            date_from: dateFrom,
            date_to: dateTo,
            row_limit: rowLimit,
            unit_option,
        };

        return this.http
            .post('/custom-reports/transfers-by-item/data', body, { headers })
            .pipe(catchError((error: any) => this.errorHandlerService.getErrorMessage(error)));
    }

    public fetchNoSaleByEmployeeData(request: INoSaleByEmployeeRequest): Observable<INoSaleByEmployeeResponse> {
        const { company, sites, date_from, date_to } = request;
        const headers = new HttpHeaders({
            'x-company-id': company,
        });
        const body = {
            sites,
            date_from,
            date_to,
            row_limit: rowLimit,
        };

        return this.http.post<INoSaleByEmployeeResponse>('/custom-reports/no-sale-by-employee', body, { headers }).pipe(
            catchError((error: { statusText: string }) => {
                return of({
                    build: '',
                    data: [],
                    page_amount: 0,
                    total_amount: 0,
                    error: this.errorHandlerService.getErrorMessage(error),
                } as INoSaleByEmployeeResponse);
            }),
        );
    }

    public fetchPhysicalSalesAccountingData(request) {
        const { company, event_ids, Site_Hierarchy_Level, Revenue_Gl_Accounts, stand_worksheet_post_closure_setting } =
            request;
        const headers = new HttpHeaders({
            'x-company-id': company,
        });

        const body = {
            event_ids: event_ids,
            site_hierarchies: Site_Hierarchy_Level,
            revenue_gl_accounts: Revenue_Gl_Accounts,
            stand_worksheet_post_closure_setting: stand_worksheet_post_closure_setting,
        };

        return this.http
            .post(`/custom-reports/physical-sales-accounting/data`, body, { headers })
            .pipe(catchError((error: any) => this.errorHandlerService.getErrorMessage(error)));
    }

    public fetchCreditCardExpirationData(request) {
        const { company } = request;
        const headers = new HttpHeaders({
            'x-company-id': company,
        });
        const body = {
            ...omit(request, ['company']),
        };

        return this.http
            .post('/custom-reports/suite-catering/credit-card-expiration/list', body, { headers })
            .pipe(catchError((error: any) => this.errorHandlerService.getErrorMessage(error)));
    }

    public fetchInventoryPurchaseSummaryData(request) {
        const { company } = request;
        const headers = new HttpHeaders({
            'x-company-id': company,
        });
        const body = {
            ...omit(request, ['company']),
        };

        return this.http
            .post('/custom-reports/inventory-purchase-summary/data', body, { headers })
            .pipe(catchError((error: any) => this.errorHandlerService.getErrorMessage(error)));
    }

    public fetchInventoryPurchaseSummaryDetail(request) {
        const { company_id, vendor_entity_id, item_entity_id, measure, date_from, date_to } = request;
        const headers = new HttpHeaders({
            'x-company-id': company_id,
        });
        const body = {
            measure,
            item_entity_id,
            vendor_entity_id,
            date_from,
            date_to,
        };

        return this.http
            .post(`/custom-reports/inventory-purchase-summary/detail`, body, { headers })
            .pipe(catchError((error: any) => this.errorHandlerService.getErrorMessage(error)));
    }
    public fetchTransferPickSheetData(request) {
        const { company, event_id, from_site_id, site_ids, transfer_type, major_category_ids, minor_category_ids } =
            request;

        if (isEmpty(event_id)) {
            return of({ data: [], rows_amount: 0, total_amount: 0 });
        }

        const headers = new HttpHeaders({
            'x-company-id': company,
        });

        const body = {
            event_id,
            transfer_type,
            major_category_ids,
            minor_category_ids,
            from_site_id,
            to_site_ids: site_ids,
        };

        return this.http
            .post(`/custom-reports/transfer-pick-sheet/data`, body, { headers })
            .pipe(catchError((error: any) => this.errorHandlerService.getErrorMessage(error)));
    }

    public fetchInventoryCostOfGoodsSoldData(request) {
        const {
            company,
            sites,
            event_ids,
            costing_method,
            item_number,
            item_name,
            isCompanyView,
            stand_worksheet_post_closure_setting,
        } = request;
        const headers = new HttpHeaders({
            'x-company-id': company,
        });
        const body = {
            item_number,
            item_name,
            event_ids,
            costing_method,
            stand_worksheet_post_closure_setting,
            row_limit: rowLimit,
            ...(!isCompanyView && {
                sites,
            }),
        };

        const endpoint = `/custom-reports/inventory-cost-of-goods-sold/${isCompanyView ? 'company' : 'site'}-view-data`;

        return this.http
            .post(endpoint, body, { headers })
            .pipe(catchError((error: any) => this.errorHandlerService.getErrorMessage(error)));
    }
}
